/**
 * Карточка референса `/lib/ref/:itemId`: оригинал, вердикт, теги, заметка, потрачено
 * и вход в генерацию «наше платье в интерьере референса». Раскладка как у `PinPage`.
 *
 * Вердикт и теги правятся локальными мутациями с патчем кэша карточки, заметка —
 * `NoteEditor` (он сам обновляет `["library"]`; карточку перезапрашиваем по успеху).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { FittingFeed } from "@/components/fitting/FittingFeed";
import { NoteEditor } from "@/components/library/NoteEditor";
import { RetryImg } from "@/components/trends/PinGrid";
import { TagPicker } from "@/components/trends/TagPicker";
import { btn, btnGhost, btnPrimary, ErrorLine, Loading, Panel } from "@/components/trends/ui";
import { AUTO_MARK, AUTO_TITLE, groupTagIds, isAuto, patchManual } from "@/lib/autotag";
import { fittingPath } from "@/lib/fitting";
import { plural, setVerdict, tagItems, untagItems, verdictLabel, type Verdict } from "@/lib/library";
import { mediaUrl } from "@/lib/media";
import { usd } from "@/lib/project";
import { referenceKeys, loadReferenceCard, type ReferenceCard } from "@/lib/references";
import { loadTags, tagKeys, tagStyle, type TagBy } from "@/lib/tags";
import { retryQuery } from "@/lib/trends";

/** Длинная сторона, ниже которой картинка мелковата для сцены (как в `RefSceneRow`). */
const SMALL_SIDE = 1000;

export function ReferencePage() {
  const id = Number(useParams().itemId);
  const valid = Number.isInteger(id) && id > 0;
  const card = useQuery({
    queryKey: referenceKeys.card(id),
    queryFn: () => loadReferenceCard(id),
    retry: retryQuery,
    enabled: valid,
  });

  if (!valid)
    return (
      <div className="p-5">
        <ErrorLine error={new Error("Неверный адрес референса.")} />
      </div>
    );
  if (card.isPending) return <Loading />;
  if (card.isError)
    return (
      <div className="grid gap-3 p-5">
        <Link to="/lib" className="inline-flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg">
          <ArrowLeft size={13} />
          Библиотеки
        </Link>
        <ErrorLine error={card.error} />
      </div>
    );
  return <ReferenceView card={card.data} />;
}

function ReferenceView({ card }: { card: ReferenceCard }) {
  const { item, library, spent } = card;
  const queryClient = useQueryClient();
  const key = referenceKeys.card(item.id);
  const tagsQuery = useQuery({ queryKey: tagKeys.list, queryFn: loadTags });
  const tags = tagsQuery.data?.tags ?? [];
  const tagNames = new Map(tags.map((t) => [t.id, t.name]));
  const [picking, setPicking] = useState(false);

  const patchItem = (fn: (i: ReferenceCard["item"]) => ReferenceCard["item"]) =>
    queryClient.setQueryData<ReferenceCard>(key, (old) => (old ? { ...old, item: fn(old.item) } : old));
  // Ручная метка: `tag_ids` и `auto_tag_ids` разом (ручная главнее авто, вытесняет автометки группы).
  const patchTag = (tagId: number, add: boolean) => {
    patchItem((i) => patchManual(i, tagId, add, groupTagIds(tags, tagId)));
    void queryClient.invalidateQueries({ queryKey: tagKeys.list });
    void queryClient.invalidateQueries({ queryKey: ["library"] });
  };

  const verdict = useMutation({
    mutationFn: (v: Verdict | null) => setVerdict([item.id], v),
    onSuccess: (_got, v) => {
      patchItem((i) => ({ ...i, verdict: v }));
      void queryClient.invalidateQueries({ queryKey: ["library"] });
    },
  });
  const tag = useMutation({
    mutationFn: (by: TagBy) => tagItems([item.id], by),
    onSuccess: (got) => patchTag(got.tag.id, true),
  });
  const untag = useMutation({
    mutationFn: (tagId: number) => untagItems([item.id], tagId),
    onSuccess: (_got, tagId) => patchTag(tagId, false),
  });

  const w = item.asset_width;
  const h = item.asset_height;
  const small = w !== null && h !== null && Math.max(w, h) < SMALL_SIDE;
  const original = mediaUrl(item.asset_path);

  return (
    <div className="mx-auto grid max-w-[1200px] gap-4 p-5" data-annot="карточка референса">
      <Link to="/lib" className="inline-flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg">
        <ArrowLeft size={13} />
        Библиотеки
      </Link>

      <div className="grid items-start gap-5 md:grid-cols-[minmax(0,1fr)_360px]">
        <div className="grid justify-items-center gap-2">
          <a href={original} target="_blank" rel="noreferrer" title="Открыть оригинал" className="block max-w-full">
            <RetryImg
              src={original}
              alt={item.note ?? ""}
              className="max-h-[calc(100vh-8rem)] max-w-full rounded-[var(--radius-card)] object-contain shadow-2xl"
              style={w && h ? { aspectRatio: `${w} / ${h}` } : undefined}
            />
          </a>
          {w !== null && h !== null ? <div className="text-[12px] tnum text-fg-faint">{w}×{h}</div> : null}
          {small ? (
            <div className="max-w-[520px] text-center text-[12px] text-warn">
              картинка {w}×{h} — мелкая для сцены: детали интерьера в генерации будут приблизительными; референс из
              Pinterest лучше положить заново кнопкой «в библиотеку» — она качает оригинал
            </div>
          ) : null}
        </div>

        <div className="grid gap-3 text-[12.5px]">
          <div className="grid gap-1" data-annot="библиотека референса">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-fg-muted">
              <Link to="/lib" className="hover:text-fg hover:underline">
                Библиотеки
              </Link>
              <span>›</span>
              <Link to={`/lib/${library.id}`} className="font-medium text-fg hover:underline">
                {library.name}
              </Link>
              <span className="rounded-[4px] bg-neutral-bg px-1.5 text-[11.5px] text-fg-muted">
                {library.category_name}
              </span>
            </div>
            {library.note ? <div className="text-[12px] text-fg-faint">{library.note}</div> : null}
          </div>

          <div className="flex flex-wrap items-center gap-1" data-annot="вердикт">
            {(["works", "fails"] as const).map((v) => {
              const on = item.verdict === v;
              return (
                <button
                  key={v}
                  type="button"
                  className={`${btnGhost} ${on ? (v === "works" ? "text-ok" : "text-err") : ""}`}
                  disabled={verdict.isPending}
                  aria-pressed={on}
                  title={on ? "Снять оценку" : `Оценить: ${verdictLabel[v]}`}
                  onClick={() => verdict.mutate(on ? null : v)}
                >
                  {verdictLabel[v]}
                </button>
              );
            })}
          </div>
          <ErrorLine error={verdict.error} />

          <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-3" data-annot="теги референса">
            {item.tag_ids.map((id) => (
              <button
                key={id}
                type="button"
                className="max-w-full truncate rounded-[3px] px-1.5 text-[11.5px] leading-[18px]"
                style={isAuto(item, id) ? { ...tagStyle(id), ...AUTO_MARK } : tagStyle(id)}
                title={isAuto(item, id) ? `${AUTO_TITLE} — клик снимет тег` : "Снять тег"}
                disabled={untag.isPending}
                onClick={() => untag.mutate(id)}
              >
                {tagNames.get(id) ?? `#${id}`} ×
              </button>
            ))}
            <div className="relative">
              <button
                type="button"
                className={btn}
                aria-expanded={picking}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setPicking((v) => !v)}
              >
                <Plus size={13} />
                Тег
              </button>
              {picking ? (
                <TagPicker
                  tags={tags}
                  // Только-авто метки — «не висят»: строка тега закрепит их вручную.
                  counts={new Map(item.tag_ids.filter((id) => !isAuto(item, id)).map((id) => [id, 1]))}
                  total={1}
                  mode="add"
                  busy={tag.isPending}
                  onPick={(id) => {
                    // Стоит только авто — повесить вручную (закрепить), иначе метка уже ручная.
                    if (!item.tag_ids.includes(id) || isAuto(item, id)) tag.mutate({ tagId: id });
                    setPicking(false);
                  }}
                  onCreate={(name) => {
                    tag.mutate({ name });
                    setPicking(false);
                  }}
                  onClose={() => setPicking(false)}
                />
              ) : null}
            </div>
          </div>
          <ErrorLine error={tag.error ?? untag.error} />

          <div className="border-t border-border pt-3" data-annot="заметка референса">
            <NoteEditor item={item} />
          </div>

          <div className="text-[12px] text-fg-muted tnum" data-annot="потрачено">
            {spent.count > 0
              ? `потрачено ${usd(spent.cost)} на ${spent.count} ${plural(spent.count, "генерацию", "генерации", "генераций")}`
              : "генераций ещё не было"}
          </div>

          <Link to={fittingPath({ refs: [item.id] })} className={`${btnPrimary} w-fit`}>
            Сгенерировать
          </Link>
        </div>
      </div>

      <Panel title="Генерации">
        <FittingFeed
          filter={{ refs: [item.id] }}
          seed={card.generations}
          emptyText="По этому референсу ещё не генерировали — нажмите «Сгенерировать»"
        />
      </Panel>
    </div>
  );
}
